import moment, { Moment } from 'moment';
import { convertFieldFormatToMoment, getDateTimeFormatInfo } from '../helpers/DateTimeUtility';
import { appContext } from '../AppContext';
import { getPropertyFormulaValue } from './components/FormView/Fields/FieldControlHelper';
import { User } from '../usersgroups/usergroup-types';
import { Form } from '../types/form-types';
import { TemplateField } from '../types/template-types';
import { FieldTypes } from '../helpers/constants';
import { getHexColorString, getOpacityFromRgba, Rgba } from '../helpers/helpers';
import { FormEditorState } from './formeditor-types';

export const analyzeSignature = (signatureValue: TypeMePlease): TypeMePlease => {
  const retval: TypeMePlease = {};
  for (let i = 0; i < signatureValue.length; i++) {
    const line = signatureValue[i];
    // Top
    if (retval.top === undefined || Number(line.y1) < retval.top) {
      retval.top = Number(line.y1);
    }
    if (retval.top === undefined || Number(line.y2) < retval.top) {
      retval.top = Number(line.y2);
    }
    // Right
    if (retval.right === undefined || Number(line.x1) > retval.right) {
      retval.right = Number(line.x1);
    }
    if (retval.right === undefined || Number(line.x2) > retval.right) {
      retval.right = Number(line.x2);
    }
    // Bottom
    if (retval.bottom === undefined || Number(line.y1) > retval.bottom) {
      retval.bottom = Number(line.y1);
    }
    if (retval.bottom === undefined || Number(line.y2) > retval.bottom) {
      retval.bottom = Number(line.y2);
    }
    // Left
    if (retval.left === undefined || Number(line.x1) < retval.left) {
      retval.left = Number(line.x1);
    }
    if (retval.left === undefined || Number(line.x2) < retval.left) {
      retval.left = Number(line.x2);
    }
  }

  retval.right += 1;
  retval.bottom += 1;
  retval.height = retval.bottom - retval.top;
  retval.width = retval.right - retval.left;
  return retval;
};

export const parseSignatureValue = (value): any[] => {
  const lines = [];
  if (value) {
    const arr = value.split('\n');
    for (let i = 0; i < arr.length; i++) {
      const coordLine = arr[i];
      if (coordLine) {
        const coords = coordLine.split(' ');
        if (Number(coords[0]) >= 0 && Number(coords[1]) >= 0 && Number(coords[2]) >= 0 && Number(coords[3]) >= 0) {
          const x1 = Number(coords[0]);
          const y1 = Number(coords[1]);
          const x2 = Number(coords[2]);
          const y2 = Number(coords[3]);

          lines.push({
            x1,
            y1,
            x2,
            y2
          });
        }
      }
    }
  }
  return lines;
};

const getReadableDate = (date) => {
  const dateTimeFormat = getDateTimeFormatInfo();
  return moment
    .utc(date)
    .local()
    .calendar(null, {
      sameDay: `[today at] ${dateTimeFormat.shortTimePattern}`,
      lastDay: `[yesterday at] ${dateTimeFormat.shortTimePattern}`,
      lastWeek: `[on] ${dateTimeFormat.shortDatePattern} [at] ${dateTimeFormat.shortTimePattern}`,
      sameElse: `[on] ${dateTimeFormat.shortDatePattern} [at] ${dateTimeFormat.shortTimePattern}`
    });
};

export interface Status {
  Status?: string;
  Completed: boolean;
  Icon: string;
}

export const getStatus = (user: User, form: Form, userMap: Record<string, TypeMePlease>, isUnsaved?: boolean): Status => {
  const retval: Status = { Completed: !!form.completedDate, Icon: form.completedDate ? 'complete' : 'draft' };
  const ownerId = form.userId ? form.userId : form.groupId;
  const ownerName = userMap ? userMap[ownerId] : null;

  if (form.versionId) {
    retval.Status = `Version ${form.versionNumber} - Saved ${getReadableDate(form.lastUpdatedDate)}`;
  } else if (form.completedDate) {
    if (user.id === ownerId || !ownerId) {
      retval.Status = `Completed ${getReadableDate(form.completedDate)}`;
    } else {
      retval.Status = `Completed by ${ownerName} ${getReadableDate(form.completedDate)}`;
    }
    retval.Icon = 'complete';
    retval.Completed = true;
  } else if (isUnsaved) {
    retval.Status = 'You have unsaved changes';
    retval.Icon = 'draft';
  } else if (userMap && form.transferHistory && form.transferHistory.length > 0) {
    const index = form.transferHistory.length - 1;
    const lastTH = form.transferHistory[index];
    if (moment(`${form.lastUpdatedDate}Z`).isAfter(moment(lastTH.date).add(30, 'seconds'))) {
      if (user.id === ownerId) {
        retval.Status = `Last saved ${getReadableDate(form.lastUpdatedDate)}`;
      } else {
        retval.Status = `Last saved by ${ownerName} ${getReadableDate(form.lastUpdatedDate)}`;
      }
      retval.Icon = 'draft';
    } else {
      if (user.id === ownerId) {
        retval.Status = `Transferred from ${userMap[lastTH.sourceId]}`;
      } else {
        retval.Status = `Transferred from ${userMap[lastTH.sourceId]} to ${ownerName}`;
      }
      retval.Icon = 'draft';
    }
  } else if (form.lastUpdatedDate) {
    if (user.id === ownerId) {
      retval.Status = `Last saved ${getReadableDate(form.lastUpdatedDate)}`;
    } else {
      retval.Status = `Last saved by ${ownerName} ${getReadableDate(form.lastUpdatedDate)}`;
    }
    retval.Icon = 'draft';
  } else {
    retval.Status = 'Unsaved form';
    retval.Icon = 'draft';
  }

  return retval;
};

export function checkIfCalculationValueIsTrue(value) {
  return !!(value && (value.toLowerCase() === 'true' || Number(value) > 0));
}

export function isVisible(formulaValues, entity) {
  const visibleFormula = getPropertyFormulaValue(formulaValues, entity.id, 'Visible');
  return !((visibleFormula.hasFormula && !checkIfCalculationValueIsTrue(visibleFormula.value)) || (!visibleFormula.hasFormula && entity.visible === false));
}

/**
 * Determines if a given field or column on a form is editable
 *
 * @param form The form.
 * @param fieldOrColumn The template field or template column.
 * @param value The value that is being set.
 * @param userId The current user id.
 * @param isAdmin True, if the current user is a admin, false otherwise.
 */
export function getEditableValue(form: Form, fieldOrColumn, value, userId, isAdmin) {
  // todo - the name of this function is misleading.

  const state = appContext.store.getState();

  // check to see if this is the hipaa modal that's requesting if the fields are editable
  // the only field that can be edited on the hipaa form is the signature field
  if (state.formeditor.hipaaForm?.signatureFieldName === (fieldOrColumn as TemplateField).name && fieldOrColumn.type === FieldTypes.Signature) {
    return true;
  }

  if (form.completedDate || form.versionId) {
    return false;
  }

  if (userId && form.userId !== userId) {
    return false;
  }
  // only lock if not admin
  if (!isAdmin) {
    if (fieldOrColumn.lockFirstValue && value) {
      return false;
    }
    if (fieldOrColumn.locked) {
      return false;
    }

    // if createdBy is not populated then this is a new form
    if (fieldOrColumn.authorOnly && form.createdBy && form.createdBy !== userId) {
      return false;
    }
  }
  if (state.formeditor.sharedAccessToken) {
    const editableFieldIds = getEditableFieldIds(state.formeditor);
    const editableColumnIds = getEditableColumnIds(state.formeditor);
    if (editableFieldIds.find((x) => x === fieldOrColumn.id) || editableColumnIds.find((x) => x === fieldOrColumn.id)) {
      return true;
    }
    return false;
  }
  return true;
}

export function getEditableFieldIds(state: FormEditorState) {
  const editableFieldIds = Object.keys(state.policyInfo?.fields ?? {});
  return editableFieldIds;
}

export function getEditableColumnIds(state: FormEditorState) {
  const customFields = state.policyInfo?.fields;

  if (!customFields) {
    return [];
  }

  return Object.values(customFields).flatMap((field: TemplateField) => field.columns?.map((column) => column.id) || []);
}

export function isTableEditable(form: Form, field: TemplateField, userId: string, isAdmin: boolean) {
  if (form.completedDate) {
    return false;
  }

  if (userId && form.userId !== userId) {
    return false;
  }
  // only lock if not admin
  if (!isAdmin) {
    if (field.locked) {
      return false;
    }

    // if createdBy is not populated then this is a new form
    if (field.authorOnly && form.createdBy && form.createdBy !== userId) {
      return false;
    }
  }
  const state = appContext.store.getState();
  if (state.formeditor.sharedAccessToken) {
    const columnIds = field.columns.map((x) => x.id);
    const editableColumnIds = getEditableColumnIds(state.formeditor);
    if (editableColumnIds.find((x) => x === columnIds.find((columnId) => x === columnId))) {
      return true;
    }
    return false;
  }
  return true;
}

export function parsePageRange(pageRange: string, maxPages: number): number[] {
  const pages: number[] = [];

  if (!pageRange) {
    return pages;
  }

  for (const rangeItem of pageRange.split(',')) {
    if (rangeItem.indexOf('-') > -1) {
      const range = rangeItem.trim().split('-');
      if (range.length === 2) {
        const rangeStart = parseInt(range[0].trim());
        const rangeEnd = parseInt(range[1].trim());
        if (!isNaN(rangeStart) && !isNaN(rangeEnd)) {
          for (let page = rangeStart; page <= rangeEnd; page++) {
            pages.push(page);
          }
        }
      }
    } else {
      const page = parseInt(rangeItem.trim());
      if (!isNaN(page)) {
        pages.push(page);
      }
    }
  }

  return pages.filter((value, index, self) => value !== 0 && value <= maxPages && self.indexOf(value) === index).sort((a, b) => a - b);
}

export function getDateTimeValue(fieldType: number, calculation, dateTimeFormat: string): Moment {
  if (calculation?.RawResult?.DateValue && calculation?.RawResult?.DateValue !== null) {
    return fieldType === FieldTypes.Date ? calculation.RawResult.DateValue.startOf('day') : calculation.RawResult.DateValue.seconds(0).milliseconds(0);
  }

  if (!calculation?.Result) {
    return null;
  }

  const parsedDateTime = moment(calculation.Result, convertFieldFormatToMoment(dateTimeFormat));
  if (parsedDateTime.isValid()) {
    return parsedDateTime;
  }

  return null;
}

/**
 * Converts a number of seconds to a legible m:ss format.
 *
 * @param seconds The number of seconds.
 */
export function convertSecondsToTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs >= 10 ? secs : '0' + secs}`;
}

/**
 * Determines the background color of a field.
 *
 * `interactive` should only be `false` when going through the send a form v2 flow.
 *
 * @param isEditable True, if the field is editable, false otherwise.
 * @param interactive True, if the field is interactive, false otherwise.
 * @param color The background color of the field.
 */
export function determineBackgroundColor(isEditable: boolean, interactive: boolean, color: Rgba): string {
  if (!interactive) {
    return getHexColorString(color);
  }

  return isEditable ? getHexColorString(color) : 'transparent';
}

/**
 * Determines the fill opacity of a field.
 *
 * `interactive` should only be `false` when going through the send a form v2 flow.
 *
 * @param isEditable True, if the field is editable, false otherwise.
 * @param interactive True, if the field is interactive, false otherwise.
 * @param color The color color of the field.
 */
export function determineFillOpacity(isEditable: boolean, interactive: boolean, color: Rgba) {
  if (!interactive) {
    return getOpacityFromRgba(color);
  }

  return isEditable ? getOpacityFromRgba(color) : 0;
}
